import { coerceSave, defaultSave, SAVE_KEY, type SaveState } from "./schema";
import { coerceStore, emptyStore, STORE_KEY, storeFromSave, type Store } from "./store";

export interface Storage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadSave(storage: Storage | null = browserStorage()): SaveState {
  try {
    const raw = storage?.getItem(SAVE_KEY);
    if (raw) return coerceSave(JSON.parse(raw));
  } catch {
    /* fall through to defaults */
  }
  return defaultSave();
}

export function persistSave(state: SaveState, storage: Storage | null = browserStorage()): void {
  try {
    storage?.setItem(SAVE_KEY, JSON.stringify(state));
  } catch {
    /* storage may be full or blocked; the game keeps working in memory */
  }
}

/**
 * The device store. On first run after the update, the older single save (if
 * any) becomes the only child; it is left in place as a backup.
 */
export function loadStore(storage: Storage | null = browserStorage()): Store {
  try {
    const raw = storage?.getItem(STORE_KEY);
    if (raw) return coerceStore(JSON.parse(raw));
    const old = storage?.getItem(SAVE_KEY);
    if (old) return storeFromSave(JSON.parse(old));
  } catch {
    /* fall through to a fresh store */
  }
  return emptyStore();
}

export function persistStore(store: Store, storage: Storage | null = browserStorage()): void {
  try {
    storage?.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    /* storage may be full or blocked; the game keeps working in memory */
  }
}
